import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { endpoints, API } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Download, Search, Activity, Mail, Clock, MousePointerClick, Eye, Send, X, MailX, Sparkles, Stethoscope } from "lucide-react";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

const fmtTime = (sec) => {
  if (!sec) return "0s";
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function AdminUsersTab() {
  const { isRTL } = useI18n();
  const { data: users = [], isLoading } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: endpoints.adminUsers,
    refetchInterval: 30_000,
  });
  const [query, setQuery] = useState("");
  const [openUser, setOpenUser] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [composerOpen, setComposerOpen] = useState(false);
  const [reengageOpen, setReengageOpen] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.email || "").toLowerCase().includes(q) ||
        (u.name || "").toLowerCase().includes(q)
    );
  }, [users, query]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((u) => selected.has(u.user_id));
  const someFilteredSelected = filtered.some((u) => selected.has(u.user_id));

  const toggleOne = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach((u) => next.delete(u.user_id));
      } else {
        filtered.forEach((u) => next.add(u.user_id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const onExport = () => {
    const url = endpoints.adminExportUsersUrl();
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="admin-users-tab">
      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C544F]" />
          <Input
            placeholder={isRTL ? "ابحث بالاسم أو الإيميل" : "Search by name or email"}
            className="ps-9 rounded-full bg-white border-[#2A2421]/15"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="admin-users-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
        <Button
          onClick={onExport}
          className="rounded-full bg-[#2A2421] hover:bg-[#2A2421]/90 text-[#F9F6F0]"
          data-testid="admin-export-users-btn"
        >
          <Download className="w-4 h-4 me-2" />
          {isRTL ? "تصدير Excel" : "Export Excel"}
        </Button>
        <Button
          onClick={() => setReengageOpen(true)}
          variant="outline"
          className="rounded-full border-[#C86240]/40 text-[#C86240] hover:bg-[#C86240]/10"
          data-testid="admin-reengage-open-btn"
        >
          <MailX className="w-4 h-4 me-2" />
          {isRTL ? "إعادة تفعيل الخاملين" : "Re-engage inactive"}
        </Button>
        <Button
          onClick={() => setDiagOpen(true)}
          variant="outline"
          className="rounded-full border-[#2A2421]/30 text-[#2A2421] hover:bg-[#2A2421]/5"
          data-testid="admin-email-diag-btn"
        >
          <Stethoscope className="w-4 h-4 me-2" />
          {isRTL ? "تشخيص الإيميل" : "Email diagnostics"}
        </Button>
        </div>
      </div>

      {/* Bulk action toolbar */}
      {selected.size > 0 && (
        <div
          className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-2xl bg-[#C86240]/8 border border-[#C86240]/30"
          data-testid="admin-bulk-toolbar"
        >
          <div className="text-sm font-medium text-[#2A2421]" data-testid="admin-bulk-count">
            {isRTL ? `${selected.size} مستخدم محدد` : `${selected.size} selected`}
          </div>
          <div className="flex gap-2 ms-auto">
            <Button
              size="sm"
              onClick={() => setComposerOpen(true)}
              className="rounded-full bg-[#C86240] hover:bg-[#C86240]/90 text-white"
              data-testid="admin-bulk-send-email-btn"
            >
              <Send className="w-3.5 h-3.5 me-1.5" />
              {isRTL ? "إرسال إيميل" : "Send Email"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={clearSelection}
              className="rounded-full"
              data-testid="admin-bulk-clear-btn"
            >
              <X className="w-3.5 h-3.5 me-1.5" />
              {isRTL ? "إلغاء التحديد" : "Clear"}
            </Button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-[#2A2421]/10 bg-white" data-testid="admin-users-table">
        <table className="w-full text-sm">
          <thead className="bg-[#F9F6F0] text-[#2A2421]">
            <tr className="text-start">
              <th className="p-3 w-10">
                <Checkbox
                  checked={allFilteredSelected ? true : (someFilteredSelected ? "indeterminate" : false)}
                  onCheckedChange={toggleAllFiltered}
                  data-testid="admin-users-select-all"
                  aria-label={isRTL ? "تحديد الكل" : "Select all"}
                />
              </th>
              <Th>{isRTL ? "المستخدم" : "User"}</Th>
              <Th>{isRTL ? "الإيميل" : "Email"}</Th>
              <Th>{isRTL ? "أول دخول" : "Joined"}</Th>
              <Th>{isRTL ? "آخر نشاط" : "Last seen"}</Th>
              <Th title={isRTL ? "صفحات شوهدت" : "Pageviews"}><Eye className="w-3.5 h-3.5 inline" /></Th>
              <Th title={isRTL ? "نقرات" : "Clicks"}><MousePointerClick className="w-3.5 h-3.5 inline" /></Th>
              <Th title={isRTL ? "وقت" : "Time"}><Clock className="w-3.5 h-3.5 inline" /></Th>
              <Th>{isRTL ? "تفاصيل" : "Details"}</Th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={9} className="p-6 text-center text-[#5C544F]">…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={9} className="p-6 text-center text-[#5C544F]">{isRTL ? "لا يوجد" : "No users"}</td></tr>
            ) : (
              filtered.map((u) => (
                <tr key={u.user_id} className={`border-t border-[#2A2421]/8 hover:bg-[#F9F6F0]/50 ${selected.has(u.user_id) ? "bg-[#C86240]/5" : ""}`} data-testid={`admin-user-row-${u.user_id}`}>
                  <td className="p-3">
                    <Checkbox
                      checked={selected.has(u.user_id)}
                      onCheckedChange={() => toggleOne(u.user_id)}
                      data-testid={`admin-user-select-${u.user_id}`}
                      aria-label={isRTL ? `تحديد ${u.email}` : `Select ${u.email}`}
                    />
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2 min-w-[180px]">
                      {u.picture && (
                        <img src={u.picture} alt="" className="w-7 h-7 rounded-full border" />
                      )}
                      <span className="font-medium text-[#2A2421]">{u.name || "—"}</span>
                    </div>
                  </td>
                  <td className="p-3 text-[#5C544F]">
                    <a href={`mailto:${u.email}`} className="hover:text-[#C86240] inline-flex items-center gap-1">
                      <Mail className="w-3.5 h-3.5" />
                      <span>{u.email}</span>
                    </a>
                  </td>
                  <td className="p-3 text-[#5C544F] whitespace-nowrap">{fmtDate(u.created_at)}</td>
                  <td className="p-3 text-[#5C544F] whitespace-nowrap">{fmtDate(u.last_seen || u.last_login_at)}</td>
                  <td className="p-3 text-center text-[#2A2421] font-semibold">{u.pageviews || 0}</td>
                  <td className="p-3 text-center text-[#2A2421] font-semibold">{u.clicks || 0}</td>
                  <td className="p-3 text-center text-[#2A2421] font-semibold">{fmtTime(u.time_seconds)}</td>
                  <td className="p-3">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => setOpenUser(u)}
                      data-testid={`admin-view-activity-${u.user_id}`}
                    >
                      <Activity className="w-3.5 h-3.5 me-1" /> {isRTL ? "نشاطه" : "Activity"}
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <UserActivityDialog user={openUser} onClose={() => setOpenUser(null)} />
      <EmailComposerDialog
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        userIds={Array.from(selected)}
        totalUsers={users.length}
        onSent={clearSelection}
      />
      <ReengageDialog open={reengageOpen} onClose={() => setReengageOpen(false)} />
      <EmailDiagDialog open={diagOpen} onClose={() => setDiagOpen(false)} />
    </div>
  );
}

const Th = ({ children, title }) => (
  <th title={title} className="p-3 text-start text-xs uppercase tracking-wider font-semibold whitespace-nowrap">
    {children}
  </th>
);

function EmailComposerDialog({ open, onClose, userIds, totalUsers, onSent }) {
  const { isRTL } = useI18n();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [allUsers, setAllUsers] = useState(false);
  const [sending, setSending] = useState(false);

  const recipientCount = allUsers ? totalUsers : userIds.length;

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error(isRTL ? "العنوان والمحتوى مطلوبان" : "Subject and body are required");
      return;
    }
    setSending(true);
    try {
      const html = body
        .split(/\n{2,}/)
        .map((p) => `<p style="margin:0 0 12px">${p.replace(/\n/g, "<br>")}</p>`)
        .join("");
      const res = await endpoints.adminEmailBroadcast({
        user_ids: allUsers ? [] : userIds,
        all_users: allUsers,
        subject: subject.trim(),
        body_html: html,
      });
      toast.success(
        isRTL
          ? `تم الإرسال إلى ${res.sent} من ${res.recipient_count}${res.failed ? ` (فشل ${res.failed})` : ""}`
          : `Sent ${res.sent}/${res.recipient_count}${res.failed ? ` (${res.failed} failed)` : ""}`
      );
      setSubject("");
      setBody("");
      setAllUsers(false);
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(isRTL ? "فشل الإرسال" : "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl bg-[#F9F6F0]" data-testid="admin-email-composer">
        <DialogHeader>
          <DialogTitle className="text-start">
            {isRTL ? "إرسال إيميل جماعي" : "Send broadcast email"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={allUsers}
              onCheckedChange={(v) => setAllUsers(!!v)}
              data-testid="admin-email-all-users-checkbox"
            />
            <span className="text-sm text-[#2A2421]">
              {isRTL ? `إرسال إلى كل المستخدمين (${totalUsers})` : `Send to all users (${totalUsers})`}
            </span>
          </label>
          <div className="text-xs text-[#5C544F]" data-testid="admin-email-recipient-count">
            {isRTL ? `سيُرسل إلى ${recipientCount} مستخدم` : `Will send to ${recipientCount} recipient(s)`}
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5C544F] mb-1 block">
              {isRTL ? "العنوان" : "Subject"}
            </label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={isRTL ? "مثال: رشفة جديدة في انتظارك ☕" : "e.g. A new brew awaits ☕"}
              className="bg-white"
              data-testid="admin-email-subject-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#5C544F] mb-1 block">
              {isRTL ? "المحتوى" : "Body"}
            </label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              placeholder={isRTL ? "اكتب رسالتك هنا… (يمكنك ترك أسطر فارغة بين الفقرات)" : "Write your message here…"}
              className="bg-white resize-none"
              data-testid="admin-email-body-input"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={sending}
            className="rounded-full"
            data-testid="admin-email-cancel-btn"
          >
            {isRTL ? "إلغاء" : "Cancel"}
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || (!allUsers && userIds.length === 0)}
            className="rounded-full bg-[#C86240] hover:bg-[#C86240]/90 text-white"
            data-testid="admin-email-send-btn"
          >
            <Send className="w-4 h-4 me-2" />
            {sending ? (isRTL ? "جارٍ الإرسال…" : "Sending…") : (isRTL ? "إرسال" : "Send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserActivityDialog({ user, onClose }) {
  const { isRTL } = useI18n();
  const { data: events = [], isLoading } = useQuery({
    queryKey: ["admin", "user-activity", user?.user_id],
    queryFn: () => endpoints.adminUserActivity(user.user_id),
    enabled: !!user,
  });

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-[#F9F6F0]" data-testid="admin-user-activity-dialog">
        {user && (
          <>
            <DialogHeader>
              <DialogTitle className="text-start">{user.name} · {user.email}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-4 gap-3 text-center">
              <Stat label={isRTL ? "صفحات" : "Views"} value={user.pageviews || 0} />
              <Stat label={isRTL ? "نقرات" : "Clicks"} value={user.clicks || 0} />
              <Stat label={isRTL ? "وقت" : "Time"} value={fmtTime(user.time_seconds)} />
              <Stat label={isRTL ? "آخر دخول" : "Last seen"} value={fmtDate(user.last_seen || user.last_login_at)} />
            </div>
            <div className="mt-4 max-h-96 overflow-y-auto rounded-xl border border-[#2A2421]/10 bg-white">
              <table className="w-full text-xs">
                <thead className="bg-[#F9F6F0] sticky top-0">
                  <tr>
                    <th className="p-2 text-start">{isRTL ? "النوع" : "Type"}</th>
                    <th className="p-2 text-start">{isRTL ? "المسار" : "Path"}</th>
                    <th className="p-2 text-start">{isRTL ? "العنصر" : "Label"}</th>
                    <th className="p-2 text-start">{isRTL ? "الوقت" : "Time"}</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (<tr><td colSpan={4} className="p-4 text-center text-[#5C544F]">…</td></tr>)}
                  {!isLoading && events.length === 0 && (<tr><td colSpan={4} className="p-4 text-center text-[#5C544F]">{isRTL ? "لا نشاط" : "No activity"}</td></tr>)}
                  {events.map((e, i) => (
                    <tr key={i} className="border-t border-[#2A2421]/5">
                      <td className="p-2">{e.type}</td>
                      <td className="p-2 text-[#5C544F]">{e.path || "—"}</td>
                      <td className="p-2 text-[#5C544F]" title={e.label}>{(e.label || "").slice(0, 40)}</td>
                      <td className="p-2 text-[#5C544F] whitespace-nowrap">{fmtDate(e.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-xl border border-[#2A2421]/10 bg-white p-3">
    <div className="text-[10px] uppercase tracking-widest text-[#5C544F]">{label}</div>
    <div className="text-lg font-semibold text-[#2A2421] mt-1">{value}</div>
  </div>
);

function ReengageDialog({ open, onClose }) {
  const { isRTL } = useI18n();
  const [days, setDays] = useState(7);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const p = await endpoints.adminReengagePreview(days);
        if (!cancelled) setPreview(p);
      } catch {
        if (!cancelled) toast.error(isRTL ? "تعذّر تحميل المعاينة" : "Preview failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open]);

  const refreshPreview = async () => {
    setLoading(true);
    try {
      const p = await endpoints.adminReengagePreview(days);
      setPreview(p);
    } catch {
      toast.error(isRTL ? "تعذّر التحميل" : "Failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!preview || preview.count === 0) return;
    if (!window.confirm(
      isRTL
        ? `سيتم إرسال إيميل لـ ${preview.count} مستخدم خامل. متابعة؟`
        : `${preview.count} inactive users will be emailed. Continue?`
    )) return;
    setSending(true);
    try {
      const res = await endpoints.adminReengageSend({ inactive_days: days, dry_run: false });
      toast.success(
        isRTL
          ? `تم إرسال ${res.sent} إيميل${res.failed ? ` (فشل ${res.failed})` : ""}`
          : `${res.sent} sent${res.failed ? ` (${res.failed} failed)` : ""}`
      );
      onClose();
    } catch (e) {
      toast.error(isRTL ? "فشل الإرسال" : "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-[#F9F6F0]" data-testid="reengage-dialog">
        <DialogHeader>
          <DialogTitle className="text-start flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#C86240]" />
            {isRTL ? "إعادة تفعيل المستخدمين الخاملين" : "Re-engage inactive users"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="rounded-xl bg-white border border-[#2A2421]/10 p-3 text-[#5C544F] text-xs leading-relaxed">
            {isRTL
              ? "نُرسل إيميلاً ودوداً لكل مستخدم سجّل قبل أكثر من X يومًا، ولم يقيّم أي وصفة ولم يشارك وصفته الخاصة. لن يُرسل لنفس المستخدم خلال آخر 30 يومًا."
              : "We email users who joined more than X days ago, have NOT rated any recipe and have NOT shared their own. Skipped if emailed in the last 30 days."}
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs text-[#5C544F] flex-1">
              {isRTL ? "أيام الخمول (≥)" : "Inactive days (≥)"}
            </label>
            <Input
              type="number"
              min={0}
              max={365}
              value={days}
              onChange={(e) => setDays(Math.max(0, Number(e.target.value) || 0))}
              className="w-24 bg-white"
              data-testid="reengage-days-input"
            />
            <Button onClick={refreshPreview} variant="outline" size="sm" className="rounded-full" data-testid="reengage-refresh-btn">
              {isRTL ? "تحديث" : "Refresh"}
            </Button>
          </div>
          <div className="rounded-xl bg-white border border-[#2A2421]/10 p-4" data-testid="reengage-preview-box">
            {loading ? (
              <div className="text-center text-[#5C544F] text-sm">…</div>
            ) : preview ? (
              <>
                <div className="text-center">
                  <div className="text-3xl font-semibold text-[#C86240]" data-testid="reengage-count">
                    {preview.count}
                  </div>
                  <div className="text-xs uppercase tracking-widest text-[#5C544F] mt-1">
                    {isRTL ? "مستخدم سيتلقّى الإيميل" : "users to be emailed"}
                  </div>
                </div>
                {preview.users.length > 0 && (
                  <div className="mt-3 max-h-32 overflow-auto text-xs text-[#5C544F] space-y-1 border-t border-[#2A2421]/8 pt-2">
                    {preview.users.slice(0, 10).map((u) => (
                      <div key={u.user_id} className="truncate">· {u.email}</div>
                    ))}
                    {preview.users.length > 10 && (
                      <div className="text-[#C86240]">+ {preview.users.length - 10} {isRTL ? "آخرون" : "more"}</div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center text-[#5C544F] text-xs">—</div>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={sending} className="rounded-full" data-testid="reengage-cancel-btn">
            {isRTL ? "إلغاء" : "Cancel"}
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !preview || preview.count === 0}
            className="rounded-full bg-[#C86240] hover:bg-[#C86240]/90 text-white"
            data-testid="reengage-send-btn"
          >
            <Send className="w-4 h-4 me-2" />
            {sending
              ? (isRTL ? "جارٍ الإرسال…" : "Sending…")
              : (isRTL ? `إرسال (${preview?.count || 0})` : `Send (${preview?.count || 0})`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EmailDiagDialog({ open, onClose }) {
  const { isRTL } = useI18n();
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const loadDiag = async () => {
    setLoading(true);
    setResult(null);
    try {
      const d = await endpoints.adminEmailDiagnostics();
      setDiag(d);
    } catch {
      toast.error(isRTL ? "تعذّر جلب التشخيص" : "Could not load diagnostics");
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  React.useEffect(() => {
    if (!open) return;
    setDiag(null);
    setResult(null);
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const d = await endpoints.adminEmailDiagnostics();
        if (!cancelled) setDiag(d);
      } catch {
        if (!cancelled) toast.error(isRTL ? "تعذّر جلب التشخيص" : "Could not load diagnostics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, isRTL]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleTestSend = async () => {
    if (!testTo || !testTo.includes("@")) {
      toast.error(isRTL ? "أدخل بريداً صحيحاً" : "Enter a valid email");
      return;
    }
    setTesting(true);
    setResult(null);
    try {
      const res = await endpoints.adminEmailDiagnosticsTestSend(testTo);
      setResult(res);
      if (res.ok) {
        toast.success(isRTL ? "تم الإرسال — تحقق من البريد" : "Sent — check inbox");
      } else {
        toast.error(isRTL ? "فشل الإرسال — راجع السجلات" : "Send failed — check logs");
      }
    } catch (e) {
      toast.error(isRTL ? "خطأ في الاختبار" : "Test send error");
    } finally {
      setTesting(false);
    }
  };

  const isSandbox = diag?.is_sandbox_fallback;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#F9F6F0] max-w-md" data-testid="email-diag-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#2A2421] text-start text-lg">
            {isRTL ? "تشخيص الإيميل (Production)" : "Email diagnostics (Production)"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {loading && <div className="text-[#5C544F]">{isRTL ? "جارٍ التحميل…" : "Loading…"}</div>}
          {diag && (
            <div className={`rounded-xl p-3 border ${isSandbox ? "bg-red-50 border-red-300" : "bg-green-50 border-green-300"}`} data-testid="email-diag-summary">
              <div className="font-bold mb-2">
                {isSandbox
                  ? (isRTL ? "⚠️ ما زال يستخدم Resend Sandbox!" : "⚠️ Still using Resend sandbox!")
                  : (isRTL ? "✅ يستخدم النطاق المخصص" : "✅ Using verified custom domain")}
              </div>
              <div className="space-y-1 text-xs font-mono break-all">
                <div><b>effective_sender:</b> {diag.effective_sender}</div>
                <div><b>env SENDER_EMAIL:</b> {diag.env_SENDER_EMAIL || "(unset)"}</div>
                <div><b>RESEND_API_KEY:</b> {diag.resend_api_key_masked}</div>
                <div><b>API key present:</b> {String(diag.resend_api_key_present)}</div>
              </div>
            </div>
          )}
          <div className="space-y-2 pt-2 border-t border-[#2A2421]/10">
            <div className="text-xs text-[#5C544F]">
              {isRTL ? "أرسل إيميل تجريبي إلى أي عنوان للتأكد من وصوله من Production:" : "Send a real test email from production:"}
            </div>
            <div className="flex gap-2">
              <Input
                type="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="someone@example.com"
                className="bg-white"
                data-testid="email-diag-to-input"
              />
              <Button
                onClick={handleTestSend}
                disabled={testing}
                className="rounded-full bg-[#C86240] hover:bg-[#C86240]/90 text-white shrink-0"
                data-testid="email-diag-send-btn"
              >
                <Send className="w-4 h-4 me-1.5" />
                {testing ? (isRTL ? "جارٍ…" : "Sending…") : (isRTL ? "إرسال" : "Send")}
              </Button>
            </div>
            {result && (
              <div className={`text-xs font-mono rounded p-2 ${result.ok ? "bg-green-50 text-green-900" : "bg-red-50 text-red-900"}`} data-testid="email-diag-result">
                ok: {String(result.ok)} · id: {result.email_id || "—"} · from: {result.effective_sender}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="email-diag-close-btn">
            {isRTL ? "إغلاق" : "Close"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
